import { useCallback } from 'react';
import { BoltIcon, CalendarIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { Link } from '../util/router';
import { useAuth } from '../util/auth';
import { useShowActions } from '../hooks';
import { formatDate, isPastShow } from '../util/data-factory';
import { logShowDetailsClickEvent } from '../util/events';
import showrunnerLogo from '../assets/images/SHOWRUNNER.png';
import ShowActionButtons from './ShowActionButtons';

export function ShowCover({ show, className = '' }) {
  const url = show?.image_urls?.[0];
  return url ? (
    <img src={url} alt="" loading="lazy" className={`h-full w-full object-cover ${className}`} />
  ) : (
    <div className={`flex h-full w-full items-center justify-center bg-slate-900 ${className}`}>
      <img
        src={showrunnerLogo}
        alt=""
        aria-hidden
        className="h-12 w-12 object-contain brightness-0 invert"
      />
    </div>
  );
}

export function ShowTile({ show, source, actions, variant = 'tile' }) {
  const auth = useAuth();
  const title = show.display_title || show.show_title || 'Untitled show';
  const venue = show.venues ?? {};
  const past = isPastShow(show.end_date);

  const logClick = useCallback(() => {
    void logShowDetailsClickEvent(auth.user?.id ?? null, String(show.show_id), source).catch(
      err => console.error('Event tracking failed:', err)
    );
  }, [auth.user?.id, show.show_id, source]);

  if (variant === 'past') {
    return (
      <Link to={`/show/${show.show_id}`} onClick={logClick} className="group block">
        <div className="aspect-square overflow-hidden rounded-lg bg-slate-100">
          <ShowCover show={show} className="transition-transform group-hover:scale-[1.03]" />
        </div>
        <p className="mt-2 line-clamp-1 text-sm font-medium text-slate-900">{title}</p>
        {show.end_date && (
          <p className="text-xs text-slate-500">Ended {formatDate(show.end_date, true)}</p>
        )}
      </Link>
    );
  }

  return (
    <article className="group flex flex-col">
      <Link
        to={`/show/${show.show_id}`}
        onClick={logClick}
        className="relative block aspect-[4/3] overflow-hidden rounded-lg bg-slate-100"
      >
        <ShowCover show={show} className="transition-transform group-hover:scale-[1.02]" />
        {show.recommended && (
          <span className="absolute left-2 top-2 flex items-center gap-1 rounded-sm bg-off-white px-1.5 py-0.5 text-xs">
            <BoltIcon className="w-4" /> Trending
          </span>
        )}
        {past && (
          <span className="badge badge-sm absolute right-2 top-2 normal-case">Past Show</span>
        )}
      </Link>
      <div className="mt-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/show/${show.show_id}`}
            onClick={logClick}
            className="line-clamp-2 text-lg font-medium leading-snug text-slate-900 md:text-xl"
          >
            {title}
          </Link>
          {venue.neighborhood && (
            <p className="text-sm font-semibold text-secondary-red">{venue.neighborhood}</p>
          )}
        </div>
        {actions && (
          <ShowActionButtons
            showId={show.show_id}
            showTitle={title}
            isSeen={actions.isSeen(show.show_id)}
            isSaved={actions.isSaved(show.show_id)}
            onSeenClick={() => void actions.handleSeen(show.show_id)}
            onSaveClick={() => actions.handleSave(show.show_id)}
          />
        )}
      </div>
      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-black/70 md:text-sm">
        {venue.venue_name && (
          <span className="flex items-center gap-1">
            <MapPinIcon className="w-4 shrink-0" />
            {venue.org_id ? (
              <Link to={`/org/${venue.org_id}`} className="hover:underline">
                {venue.venue_name}
              </Link>
            ) : (
              venue.venue_name
            )}
          </span>
        )}
        {show.start_date && (
          <span className="flex items-center gap-1">
            <CalendarIcon className="w-4 shrink-0" />
            {formatDate(show.start_date, true)} - {formatDate(show.end_date, true)}
          </span>
        )}
      </div>
    </article>
  );
}

function ShowGrid({ shows, source = 'grid', variant = 'tile', className = '' }) {
  const actions = useShowActions();

  const columns =
    variant === 'past'
      ? 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-6'
      : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10';

  return (
    <>
      <div className={`grid ${columns} ${className}`}>
        {shows.map(show => (
          <ShowTile
            key={show.show_id}
            show={show}
            source={source}
            variant={variant}
            actions={variant === 'past' ? null : actions}
          />
        ))}
      </div>
      {actions.modals}
    </>
  );
}

export default ShowGrid;
